import React, { useEffect, useMemo, useState } from "react";
import {
    Row, Col, Card, CardHeader, CardBody,
    Button, Form, FormGroup, Label, Input, FormText,
    Nav, NavItem, NavLink, TabContent, TabPane, Alert, Spinner,
    Modal, ModalHeader, ModalBody, ModalFooter
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../../api/urls";
import { fetchSampleContract, fetchCompanyLogo, fetchCompanyFavicon, deleteCompanyImage } from "../../api/requests/company-requests";
import { CompanyGroupSettings } from "../modals/company/CompanyGroupSettings";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PhoneInput from "../../components/Forms/PhoneInput";
import { COMPANY_TYPES, COMPANY_TYPE_LABEL_MAP } from "../../constants/companyTypes";
import { describeAxiosError } from "../../utils/apiErrors";
import { validateBrandingFile, BRANDING_ACCEPT_ATTR, formatAllowedDimensions } from "../../constants/brandingAssets";
import { DISTRICTS } from "../../constants/districts";
import PaymentSettingsTab from "../../feautures/settings/components/PaymentSettingsTab"
import CompanyEarningSettings from "./CompanyEarningSettings";
import CompanyValorSettings from "./CompanyValorSettings";
import { useProfileUser } from "../../feautures/settings/hooks/useProfileUser";
import "../../assets/css/page-common.css";
import BackButton from "../../components/Common/BackButton";
import { notifyBrandingUpdated } from "../../utils/branding";
import { formatLicenseDate } from "../../utils/subscriptionDates";

const NAVY = "#221C5D";

const CompanyEditPage = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // /companies/:id/edit
    const token = useMemo(() => localStorage.getItem("token"), []);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [fetchError, setFetchError] = useState("");
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState("company");
    // Komisyon hakedişi platformun payıdır; sekme yalnız süper admine gösterilir.
    const { roles } = useProfileUser();
    const isSuperAdmin = (roles || []).includes("ROLE_SUPER_ADMIN");
    const [city, setCity] = useState("");
    const [district, setDistrict] = useState("");
    const [districtOptions, setDistrictOptions] = useState([]);

    const [company, setCompany] = useState(null);

    // Görseller
    const [logoPreview, setLogoPreview] = useState(null);
    const [faviconPreview, setFaviconPreview] = useState(null);
    const [logoError, setLogoError] = useState(false);
    const [faviconError, setFaviconError] = useState(false);
    // Silme onayı: 'logo' | 'favicon' | null
    const [pendingDelete, setPendingDelete] = useState(null);

    // Paket burada yalnız gösterilir; atama/değişiklik paket yönetiminden yapılır.
    const [currentPlanName, setCurrentPlanName] = useState("");

    const turkishCities = [
        "Adana","Adıyaman","Afyonkarahisar","Ağrı","Amasya","Ankara","Antalya","Artvin",
        "Aydın","Balıkesir","Bilecik","Bingöl","Bitlis","Bolu","Burdur","Bursa","Çanakkale",
        "Çankırı","Çorum","Denizli","Diyarbakır","Edirne","Elazığ","Erzincan","Erzurum",
        "Eskişehir","Gaziantep","Giresun","Gümüşhane","Hakkâri","Hatay","Isparta","Mersin",
        "İstanbul","İzmir","Kars","Kastamonu","Kayseri","Kırklareli","Kırşehir","Kocaeli",
        "Konya","Kütahya","Malatya","Manisa","Kahramanmaraş","Mardin","Muğla","Muş",
        "Nevşehir","Niğde","Ordu","Rize","Sakarya","Samsun","Siirt","Sinop","Sivas",
        "Tekirdağ","Tokat","Trabzon","Tunceli","Şanlıurfa","Uşak","Van","Yozgat","Zonguldak",
        "Aksaray","Bayburt","Karaman","Kırıkkale","Batman","Şırnak","Bartın","Ardahan",
        "Iğdır","Yalova","Karabük","Kilis","Osmaniye","Düzce"
    ];

    const showToast = (type, msg) => {
        const map = { danger: "error", error: "error" };
        const fn = toast[map[type] || type] || toast.info;
        fn(msg, { autoClose: 3000 });
    };

    // Form
    const [form, setForm] = useState({
        companyName: "",
        subdomain: "",
        domain: "",
        contactFullName: "",
        companyType: "",
        workingArea: "",
        contactEmail: "",
        // Lisans tarihleri artık atanan paketten türetilir; elle girilmez.
        licenseStartDate: "",
        licenseExpiryDate: "",
        active: true,
        logoUrl: "",
        faviconUrl: "",
        phoneNumber: "",
        address: "",
        // Sözleşme metinleri (artık ayrı tabta düzenlenecek)
        cancellationReturnPrivacy: "",
        personalDataPolicy: "",
        serviceContract: "",
        onlineCollectionContract: "",
        // Fatura
        invoiceTaxNumber: "",
        invoiceTcNumber: "",
        invoiceTaxOffice: "",
        // Kur ayarları
        fetchRatesAutomatically: true,
        usdMarkupRate: "",
        eurMarkupRate: "",
        gbpMarkupRate: "",
        finalUsdRate: "",
        finalEurRate: "",
        finalGbpRate: ""
    });

    // Örnek sözleşme çekimi (Sözleşmeler tab’ı için)
    const CONTRACT_TYPE_MAP = {
        cancellationReturnPrivacy: "CANCELLATION_RETURN_PRIVACY",
        personalDataPolicy: "KVKK",
        serviceContract: "SERVICE_AGREEMENT",
        onlineCollectionContract: "ONLINE_COLLECTION",
    };
    const [sampleLoading, setSampleLoading] = useState(false);

    const loadSampleContract = async (field) => {
        const type = CONTRACT_TYPE_MAP[field];
        if (!type || !id) return;
        try {
            setSampleLoading(true);
            const json = await fetchSampleContract(type, id);
            if (json.data) {
                setForm((prev) => ({ ...prev, [field]: json.data }));
                showToast("success", "Örnek sözleşme yüklendi");
            } else {
                showToast("danger", json.message || "Örnek sözleşme alınamadı");
            }
        } catch {
            showToast("danger", "Bağlantı hatası");
        } finally {
            setSampleLoading(false);
        }
    };

    // Şirket detayını getir
    const fetchCompany = async () => {
        setLoading(true);
        setFetchError("");
        try {
            const { data } = await axios.get(API.admin_company(id), {
                headers: {},
            });
            if (data?.statusCode === 200) {
                const c = data.data;
                setCompany(c);

                let initialCity = "";
                let initialDistrict = "";
                let initialAddress = c.address || "";

                if (initialAddress) {
                    const lastDashIndex = initialAddress.lastIndexOf(" - ");
                    let locationPart = "";

                    if (lastDashIndex !== -1) {
                        locationPart = initialAddress.substring(lastDashIndex + 3).trim(); // "ilçe / il"
                        initialAddress = initialAddress.substring(0, lastDashIndex).trim(); // sadece açık adres
                    } else {
                        locationPart = initialAddress.trim();
                        initialAddress = "";
                    }

                    if (locationPart) {
                        const locParts = locationPart
                            .split(" / ")
                            .map(p => p.trim())
                            .filter(Boolean);

                        if (locParts.length === 2) {
                            const [districtCandidate, cityCandidate] = locParts;

                            if (turkishCities.includes(cityCandidate)) {
                                initialCity = cityCandidate;
                                const possibleDistricts = DISTRICTS[initialCity] || [];

                                if (possibleDistricts.includes(districtCandidate)) {
                                    initialDistrict = districtCandidate;
                                } else {
                                    // İlçe tanınmazsa, adresin sonuna geri ekleyelim
                                    initialAddress = initialAddress
                                        ? `${initialAddress} - ${districtCandidate}`
                                        : districtCandidate;
                                }
                            } else if (turkishCities.includes(districtCandidate)) {
                                initialCity = districtCandidate;
                                initialAddress = initialAddress
                                    ? `${initialAddress} - ${cityCandidate}`
                                    : cityCandidate;
                            }
                        } else if (locParts.length === 1) {
                            const single = locParts[0];
                            if (turkishCities.includes(single)) {
                                initialCity = single;
                            } else {
                                initialDistrict = single;
                            }
                        }
                    }
                }
                if (!initialAddress && !initialCity && !initialDistrict) {
                    initialAddress = "";
                }
                setCity(initialCity);
                setDistrict(initialDistrict);
                setDistrictOptions(initialCity ? DISTRICTS[initialCity] || [] : []);
                setCurrentPlanName(c.currentPlanName || "");
                setForm({
                    companyName: c.companyName || "",
                    subdomain: c.subdomain || "",
                    domain: c.domain || "",
                    contactFullName: c.contactFullName || "",
                    companyType: c.companyType || "",
                    workingArea: c.workingArea || "",
                    contactEmail: c.contactEmail || "",
                    licenseStartDate: c.licenseStartDate || "",
                    licenseExpiryDate: c.licenseExpiryDate || "",
                    active: c.active !== undefined ? c.active : true,
                    logoUrl: c.logoUrl || "",
                    faviconUrl: c.faviconUrl || "",
                    phoneNumber: c.phoneNumber || "",
                    address: initialAddress,
                    cancellationReturnPrivacy: c.cancellationReturnPrivacy || "",
                    personalDataPolicy: c.personalDataPolicy || "",
                    serviceContract: c.serviceContract || "",
                    onlineCollectionContract: c.onlineCollectionContract || "",
                    invoiceTaxNumber: c.invoiceTaxNumber || "",
                    invoiceTcNumber: c.invoiceTcNumber || "",
                    invoiceTaxOffice: c.invoiceTaxOffice || "",
                    fetchRatesAutomatically:
                        c.fetchRatesAutomatically !== undefined ? c.fetchRatesAutomatically : true,
                    usdMarkupRate: c.usdMarkupRate || "",
                    eurMarkupRate: c.eurMarkupRate || "",
                    gbpMarkupRate: c.gbpMarkupRate || "",
                    finalUsdRate: c.finalUsdRate || "",
                    finalEurRate: c.finalEurRate || "",
                    finalGbpRate: c.finalGbpRate || "",
                });

                // görseller
                try {
                    const resL = await fetchCompanyLogo(id);
                    if (resL.ok && resL.headers.get("Content-Type")?.startsWith("image/")) {
                        const blob = await resL.blob();
                        setLogoPreview(URL.createObjectURL(blob));
                        setLogoError(false);
                    } else {
                        setLogoPreview(null);
                        setLogoError(true);
                    }
                } catch {
                    setLogoPreview(null);
                    setLogoError(true);
                }

                try {
                    const resF = await fetchCompanyFavicon(id);
                    if (resF.ok && resF.headers.get("Content-Type")?.startsWith("image/")) {
                        const blob = await resF.blob();
                        setFaviconPreview(URL.createObjectURL(blob));
                        setFaviconError(false);
                    } else {
                        setFaviconPreview(null);
                        setFaviconError(true);
                    }
                } catch {
                    setFaviconPreview(null);
                    setFaviconError(true);
                }
            } else {
                setFetchError("Firma bilgileri getirilemedi");
            }
        } catch (err) {
            if (err.response?.status === 401 || err.response?.status === 403) {
                navigate("/login");
            } else {
                setFetchError(err.response?.data?.message || "Firma bilgileri alınırken hata oluştu");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = "Firma Düzenle | Tahsilat360";
        fetchCompany();
    }, [id]);

    // Handlers
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === "city") {
            const newCity = value;
            setCity(newCity);
            setDistrict("");
            setDistrictOptions(DISTRICTS[newCity] || []);
        } else if (name === "district") {
            setDistrict(value);
        } else {
            setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
        }
        if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!form.companyName.trim()) newErrors.companyName = "Firma adı zorunludur";
        if (!form.subdomain.trim()) newErrors.subdomain = "Subdomain alanı zorunludur";
        if (!form.domain.trim()) newErrors.domain = "Domain alanı zorunludur";
        if (!form.contactFullName.trim()) newErrors.contactFullName = "İletişim kişi adı zorunludur";
        if (!form.companyType) {
               newErrors.companyType = "Firma tipi zorunludur";
             } else if (!COMPANY_TYPES.some(t => t.value === form.companyType)) {
               newErrors.companyType = "Geçersiz firma tipi";
             }
        if (!form.workingArea) newErrors.workingArea = "Faaliyet alanı zorunludur";
        if (!form.contactEmail.trim()) newErrors.contactEmail = "İletişim e-posta adresi zorunludur";

        if (form.companyName.length > 120) newErrors.companyName = "Firma adı en fazla 120 karakter olabilir";
        if (form.contactFullName.length > 120) newErrors.contactFullName = "İletişim kişi adı en fazla 120 karakter olabilir";
        if (form.contactEmail.length > 120) newErrors.contactEmail = "İletişim e-posta adresi en fazla 120 karakter olabilir";
        if (form.address?.length > 255) newErrors.address = "Adres en fazla 255 karakter olabilir";

        if (form.invoiceTaxOffice.length > 100) newErrors.invoiceTaxOffice = "Vergi dairesi en fazla 100 karakter olabilir";

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (form.contactEmail && !emailRegex.test(form.contactEmail)) {
            newErrors.contactEmail = "Lütfen geçerli bir e-posta adresi giriniz";
        }

        const subdomainPattern = /^[a-z0-9-]{3,100}$/;
        if (form.subdomain && !subdomainPattern.test(form.subdomain)) {
            newErrors.subdomain = "Subdomain yalnızca küçük harf, rakam ve tire (-) içermelidir (3-100 karakter)";
        }

        const domainPattern = /^[a-z0-9.-]{3,100}$/i;
        if (form.domain && !domainPattern.test(form.domain)) {
            newErrors.domain = "Domain yalnızca harf, rakam, nokta ve tire (-) içermelidir (3-100 karakter)";
        }

        if (form.phoneNumber) {
            const phonePattern = /^\+?\d{7,15}$/;
            if (!phonePattern.test(form.phoneNumber)) {
                newErrors.phoneNumber = "Telefon numarası + ile başlayabilir ve 7-15 rakamdan oluşmalıdır";
            }
        }

        if (form.invoiceTaxNumber) {
            const taxNumberPattern = /^\d{10}$/;
            if (!taxNumberPattern.test(form.invoiceTaxNumber)) {
                newErrors.invoiceTaxNumber = "Vergi numarası 10 haneli olmalıdır";
            }
        }

        if (form.invoiceTcNumber) {
            const tcNumberPattern = /^\d{11}$/;
            if (!tcNumberPattern.test(form.invoiceTcNumber)) {
                newErrors.invoiceTcNumber = "TC kimlik numarası 11 haneli olmalıdır";
            }
        }

        if ((form.cancellationReturnPrivacy || "").length > 10000)
            newErrors.cancellationReturnPrivacy = "İptal/İade koşulları en fazla 10.000 karakter olmalı";
        if ((form.personalDataPolicy || "").length > 10000)
            newErrors.personalDataPolicy = "Kişisel veriler politikası en fazla 10.000 karakter olmalı";
        if ((form.serviceContract || "").length > 10000)
            newErrors.serviceContract = "Hizmet sözleşmesi en fazla 10.000 karakter olmalı";
        if ((form.onlineCollectionContract || "").length > 10000)
            newErrors.onlineCollectionContract = "Online tahsilat sözleşmesi en fazla 10.000 karakter olmalı";

        setErrors(newErrors);
        if (Object.keys(newErrors).length) {
            Object.values(newErrors).forEach((m) => showToast("warning", m));
            return false;
        }
        return true;
    };

    const uploadFile = async (file, type) => {
        const data = new FormData();
        data.append("file", file);
        const endpoint = type === "logo" ? API.company_logo(id) : API.company_favicon(id);
        const res = await axios.post(endpoint, data, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return res.data?.data;
    };

    const handleLogoSelect = async (e) => {
        const file = e.target.files?.[0];
        // Erken dönüşlerde de aynı dosya yeniden seçilebilsin diye en başta temizleniyor.
        e.target.value = "";
        if (!file) return;

        // Doğrulama önizlemeden ÖNCE: kurala uymayan bir dosya ekranda hiç görünmesin.
        const validationError = await validateBrandingFile(file, "logo");
        if (validationError) {
            showToast("danger", validationError);
            return;
        }

        // Sonuç belli olana kadar iki URL de elde: seçilen dosya gösteriliyor,
        // mevcut görselin URL'i hata durumunda geri dönmek için saklanıyor.
        const previewUrl = URL.createObjectURL(file);
        const previousUrl = logoPreview;
        setLogoPreview(previewUrl);
        setLogoError(false);

        try {
            await uploadFile(file, "logo");
            showToast("success", "Logo yüklendi");
            // Sol menüdeki logo Header'da ayrı çekiliyor; haber verilmezse eski görsel kalır.
            notifyBrandingUpdated();
            // Sabit 800 ms gecikme kaldırıldı: önbelleği atlatmıyor, yalnız yarış
            // durumu yaratıyordu. Yükleme tamamlandıktan sonra taze görsel çekiliyor.
            if (previousUrl) URL.revokeObjectURL(previousUrl);
            URL.revokeObjectURL(previewUrl);
            try {
                const res = await fetchCompanyLogo(id);
                if (res.ok && res.headers.get("Content-Type")?.startsWith("image/")) {
                    const blob = await res.blob();
                    setLogoPreview(URL.createObjectURL(blob));
                    setLogoError(false);
                }
            } catch {}
        } catch (err) {
            // Yükleme başarısızsa önizleme ESKİ görsele dönüyor. Önceden burada
            // geri alma yoktu: "başarısız" uyarısı çıkarken ekranda yeni dosya kalıyordu.
            URL.revokeObjectURL(previewUrl);
            setLogoPreview(previousUrl);
            showToast("danger", describeAxiosError(err, "Logo yüklenemedi.").message);
        }
    };

    const handleFaviconSelect = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;

        const validationError = await validateBrandingFile(file, "favicon");
        if (validationError) {
            showToast("danger", validationError);
            return;
        }

        const previewUrl = URL.createObjectURL(file);
        const previousUrl = faviconPreview;
        setFaviconPreview(previewUrl);
        setFaviconError(false);

        try {
            await uploadFile(file, "favicon");
            showToast("success", "Favicon yüklendi");
            notifyBrandingUpdated();
            if (previousUrl) URL.revokeObjectURL(previousUrl);
            URL.revokeObjectURL(previewUrl);
            try {
                const res = await fetchCompanyFavicon(id);
                if (res.ok && res.headers.get("Content-Type")?.startsWith("image/")) {
                    const blob = await res.blob();
                    setFaviconPreview(URL.createObjectURL(blob));
                    setFaviconError(false);
                }
            } catch {}
        } catch (err) {
            URL.revokeObjectURL(previewUrl);
            setFaviconPreview(previousUrl);
            showToast("danger", describeAxiosError(err, "Favicon yüklenemedi.").message);
        }
    };

    /** Logo/favicon siler; sunucu hem dosyayı hem kayıttaki yolu temizler. */
    const handleAssetDelete = async (type) => {
        const currentUrl = type === "logo" ? logoPreview : faviconPreview;
        const setPreview = type === "logo" ? setLogoPreview : setFaviconPreview;
        const setError = type === "logo" ? setLogoError : setFaviconError;
        const label = type === "logo" ? "Logo" : "Favicon";

        try {
            const res = await deleteCompanyImage(id, type);
            if (!res.ok) {
                const json = await res.json().catch(() => null);
                showToast("danger", json?.message || `${label} silinemedi.`);
                return;
            }
            if (currentUrl) URL.revokeObjectURL(currentUrl);
            setPreview(null);
            setError(false);
            notifyBrandingUpdated();
            showToast("success", `${label} silindi`);
        } catch (err) {
            showToast("danger", describeAxiosError(err, `${label} silinemedi.`).message);
        }
    };

    const downloadBlobUrl = (url, filename) => {
        if (!url) return;
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setSaving(true);
        try {
            const submitData = { ...form };

            const baseAddress = submitData.address?.trim() || "";
            const ilIlcePart =
                city && district
                    ? `${district} / ${city}`
                    : city
                        ? city
                        : district || "";

            submitData.address = baseAddress
                ? ilIlcePart
                    ? `${baseAddress} - ${ilIlcePart}`
                    : baseAddress
                : ilIlcePart || null;

            // İstemci temizlikleri
            ["logoUrl", "faviconUrl"].forEach((k) => delete submitData[k]);
            ["usdMarkupRate", "eurMarkupRate", "gbpMarkupRate", "finalUsdRate", "finalEurRate", "finalGbpRate"].forEach((k) => {
                submitData[k] = submitData[k] !== "" && submitData[k] !== null ? parseFloat(submitData[k]) : null;
            });
            if (submitData.licenseStartDate === "") submitData.licenseStartDate = null;
            if (submitData.licenseExpiryDate === "") submitData.licenseExpiryDate = null;

            // Lisans tarihleri ve paket burada değiştirilemez; paket yönetiminden gelir.
            delete submitData.licenseStartDate;
            delete submitData.licenseExpiryDate;

            // boş alanları null’a çek
            [
                "phoneNumber", "address", "invoiceTaxNumber", "invoiceTcNumber", "invoiceTaxOffice",
                "cancellationReturnPrivacy", "personalDataPolicy", "serviceContract", "onlineCollectionContract"
            ].forEach((k) => {
                if (!submitData[k] || (typeof submitData[k] === "string" && submitData[k].trim() === "")) submitData[k] = null;
            });

            Object.keys(submitData).forEach((k) => {
                if (submitData[k] === "") submitData[k] = null;
            });

            await axios.put(API.admin_company(id), submitData, {
                headers: {},
            });

            showToast("success", "Firma başarıyla kaydedildi");
            setErrors({});
            fetchCompany();
        } catch (err) {
            if (err.response?.data) {
                const { statusCode, message } = err.response.data;
                if (statusCode === 409) showToast("danger", message || "Veri çakışması: Subdomain/e-posta kullanımda olabilir.");
                else showToast("danger", message || "Firma kaydedilirken hata oluştu.");
            } else if (err.request) {
                showToast("danger", "Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.");
            } else {
                showToast("danger", "Firma kaydedilirken bilinmeyen bir hata oluştu.");
            }
        } finally {
            setSaving(false);
        }
    };

    const styles = `
    .company-edit .btn-navy { background:${NAVY}; border-color:${NAVY}; color:#fff; }
    .company-edit .btn-navy:hover { filter:brightness(1.03); }
    .company-edit .btn-outline-navy { color:${NAVY}; border-color:${NAVY}; }
    .company-edit .btn-outline-navy:hover { background:${NAVY}; color:#fff; }
    .company-edit .rounded-4 { border-radius:1rem!important; }
    .company-edit .pill { border-radius:999px!important; }
    .company-edit .nav-tabs .nav-link.active { color:${NAVY}; font-weight:600; }
    .company-edit .nav-tabs .nav-link { cursor:pointer; }
  `;

    return (
        <div className="page-content page-common company-edit">
            <style>{styles}</style>
            <div className="container-fluid">

                {/* ── Page Header ── */}
                <div className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-3">
                        <BackButton />
                        <div>
                            <h5 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
                                {company?.companyName || "Firma Düzenle"}
                            </h5>
                            <span className="text-muted" style={{ fontSize: ".82rem" }}>
                                Firma Düzenleme Sayfası
                            </span>
                        </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                        {["company","contracts"].includes(activeTab) && (
                            <button className="btn btn-navy btn-rounded d-flex align-items-center gap-1 px-3" style={{ height: 34, fontSize: ".8rem" }} onClick={handleSubmit} disabled={saving || loading}>
                                {saving ? (<><Spinner size="sm" className="me-1" /> Kaydediliyor...</>) : (<><i className="bx bx-save" style={{ fontSize: 16 }} /> Kaydet</>)}
                            </button>
                        )}
                    </div>
                </div>

                <Row>
                    <Col lg={12}>
                        <Card className="detail-card">
                            <CardHeader>
                                <div className="section-title">
                                    <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                                        <i className="bx bx-buildings" />
                                    </div>
                                    Firma Bilgileri
                                </div>
                            </CardHeader>

                            <CardBody className="p-4">
                                {fetchError && (
                                    <Alert color="danger" className="border-0 rounded-4">
                                        <i className="bx bx-error me-2" />
                                        {fetchError}
                                        <Button size="sm" color="danger" className="ms-2 pill" onClick={fetchCompany}>
                                            Tekrar Dene
                                        </Button>
                                    </Alert>
                                )}

                                {loading ? (
                                    <div className="text-center p-5">
                                        <Spinner color="primary" />
                                        <div className="mt-2 text-muted">Firma bilgileri yükleniyor...</div>
                                    </div>
                                ) : (
                                    <>
                                        {/* Sekmeler: mevcutlara sadece "Sözleşmeler" eklendi */}
                                        <Nav tabs className="mb-3">
                                            <NavItem>
                                                <NavLink className={activeTab === "company" ? "active" : ""} onClick={() => setActiveTab("company")}>
                                                    Firma Bilgileri
                                                </NavLink>
                                            </NavItem>

                                            {!!company && (
                                                <>
                                                    <NavItem>
                                                        <NavLink className={activeTab === "payment" ? "active" : ""} onClick={() => setActiveTab("payment")}>
                                                            Sanal POS Ayarları
                                                        </NavLink>
                                                    </NavItem>
                                                    <NavItem>
                                                        <NavLink className={activeTab === "groups" ? "active" : ""} onClick={() => setActiveTab("groups")}>
                                                            Taksit Grup Ayarları
                                                        </NavLink>
                                                    </NavItem>
                                                </>
                                            )}

                                            {/* YENİ TAB */}
                                            <NavItem>
                                                <NavLink className={activeTab === "contracts" ? "active" : ""} onClick={() => setActiveTab("contracts")}>
                                                    Sözleşmeler
                                                </NavLink>
                                            </NavItem>

                                            {/* Komisyon hakediş oranları — POS Rapor'daki kazanç kolonunu besler */}
                                            {!!company && isSuperAdmin && (
                                                <NavItem>
                                                    <NavLink className={activeTab === "earning" ? "active" : ""} onClick={() => setActiveTab("earning")}>
                                                        Komisyon Hakediş
                                                    </NavLink>
                                                </NavItem>
                                            )}

                                            {/* Valör (T+X) — firma yöneticisi kendi ayarını Ayarlar ekranından
                                                düzenler; buradaki sekme süper adminin firma seçip düzenlemesi için. */}
                                            {!!company && isSuperAdmin && (
                                                <NavItem>
                                                    <NavLink className={activeTab === "valor" ? "active" : ""} onClick={() => setActiveTab("valor")}>
                                                        Valör
                                                    </NavLink>
                                                </NavItem>
                                            )}
                                        </Nav>

                                        <TabContent activeTab={activeTab}>
                                            {/* FİRMA BİLGİLERİ (alan sırası yeniden düzenlendi) */}
                                            <TabPane tabId="company">
                                                <Form>
                                                    {/* 1) Firma Bilgileri */}
                                                    <h5 className="text-muted mb-3">Firma Bilgileri</h5>
                                                    <FormGroup>
                                                        <Label>Firma Adı *</Label>
                                                        <Input name="companyName" value={form.companyName} onChange={handleChange} maxLength={120} invalid={!!errors.companyName} />
                                                        {errors.companyName && <div className="invalid-feedback d-block">{errors.companyName}</div>}
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>Firma Tipi *</Label>
                                                        <Input
                                                            type="select"
                                                            name="companyType"
                                                            value={form.companyType}
                                                            onChange={handleChange}
                                                            invalid={!!errors.companyType}
                                                        >
                                                            <option value="">Tip seçiniz</option>
                                                            {COMPANY_TYPES.map(t => (
                                                                <option key={t.value} value={t.value}>{t.label}</option>
                                                            ))}
                                                        </Input>
                                                        {errors.companyType && <div className="invalid-feedback d-block">{errors.companyType}</div>}
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>Çalışma Alanı *</Label>
                                                        <Input type="select" name="workingArea" value={form.workingArea} onChange={handleChange} invalid={!!errors.workingArea}>
                                                            <option value="">Alan seçiniz</option>
                                                            <option value="INFORMATION_TECHNOLOGY">Bilişim ve Yazılım</option>
                                                            <option value="MANUFACTURING">Üretim ve İmalat</option>
                                                            <option value="RETAIL">Perakende Satış</option>
                                                            <option value="HEALTHCARE">Sağlık ve Tıp</option>
                                                            <option value="FINANCE">Finans ve Sigorta</option>
                                                            <option value="EDUCATION">Eğitim Hizmetleri</option>
                                                            <option value="AUTOMOTIVE">Otomotiv</option>
                                                            <option value="FOOD_AND_BEVERAGE">Gıda</option>
                                                            <option value="CONSTRUCTION">İnşaat</option>
                                                            <option value="LOGISTICS">Lojistik</option>
                                                            <option value="ENERGY">Enerji</option>
                                                            <option value="OTHER">Diğer</option>
                                                        </Input>
                                                        {errors.workingArea && <div className="invalid-feedback d-block">{errors.workingArea}</div>}
                                                    </FormGroup>

                                                    <Row className="g-3">
                                                        {form.companyType === "SOLE_PROPRIETORSHIP" ? (
                                                            <Col md={6}>
                                                                <FormGroup>
                                                                    <Label>TC Kimlik Numarası</Label>
                                                                    <Input
                                                                        name="invoiceTcNumber"
                                                                        value={form.invoiceTcNumber}
                                                                        onChange={handleChange}
                                                                        placeholder="12345678901"
                                                                        maxLength={11}
                                                                        invalid={!!errors.invoiceTcNumber}
                                                                    />
                                                                    {errors.invoiceTcNumber && <div className="invalid-feedback d-block">{errors.invoiceTcNumber}</div>}
                                                                </FormGroup>
                                                            </Col>
                                                        ) : (
                                                            /* Diğer tüm firma tipleri → sadece VKN */
                                                            <Col md={6}>
                                                                <FormGroup>
                                                                    <Label>Vergi Numarası</Label>
                                                                    <Input
                                                                        name="invoiceTaxNumber"
                                                                        value={form.invoiceTaxNumber}
                                                                        onChange={handleChange}
                                                                        placeholder="1234567890"
                                                                        maxLength={10}
                                                                        invalid={!!errors.invoiceTaxNumber}
                                                                    />
                                                                    {errors.invoiceTaxNumber && <div className="invalid-feedback d-block">{errors.invoiceTaxNumber}</div>}
                                                                </FormGroup>
                                                            </Col>
                                                        )}
                                                        <Col md={6}>
                                                            <FormGroup>
                                                                <Label>Vergi Dairesi</Label>
                                                                <Input name="invoiceTaxOffice" value={form.invoiceTaxOffice} onChange={handleChange} placeholder="Merkez Vergi Dairesi" maxLength={100} invalid={!!errors.invoiceTaxOffice} />
                                                                {errors.invoiceTaxOffice && <div className="invalid-feedback d-block">{errors.invoiceTaxOffice}</div>}
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>

                                                    <hr className="my-4" />
                                                    <h5 className="text-muted mb-3">İletişim</h5>
                                                    <FormGroup>
                                                        <Label>İletişim Kişisi Adı *</Label>
                                                        <Input name="contactFullName" value={form.contactFullName} onChange={handleChange} maxLength={120} invalid={!!errors.contactFullName} />
                                                        {errors.contactFullName && <div className="invalid-feedback d-block">{errors.contactFullName}</div>}
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>İletişim E-postası *</Label>
                                                        <Input type="email" name="contactEmail" value={form.contactEmail} onChange={handleChange} maxLength={120} invalid={!!errors.contactEmail} />
                                                        <FormText>Bildirimler bu adrese gider.</FormText>
                                                        {errors.contactEmail && <div className="invalid-feedback d-block">{errors.contactEmail}</div>}
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <PhoneInput value={form.phoneNumber} onChange={handleChange} name="phoneNumber" invalid={!!errors.phoneNumber} />
                                                        {errors.phoneNumber && <div className="invalid-feedback d-block">{errors.phoneNumber}</div>}
                                                    </FormGroup>

                                                    <Row className="g-3 mb-3">
                                                        {/* Şehir */}
                                                        <Col md={6}>
                                                            <FormGroup>
                                                                <Label>Şehir</Label>
                                                                <Input
                                                                    type="select"
                                                                    name="city"
                                                                    value={city}
                                                                    onChange={handleChange}
                                                                >
                                                                    <option value="">Seçiniz</option>
                                                                    {turkishCities.map((city) => (
                                                                        <option key={city} value={city}>
                                                                            {city}
                                                                        </option>
                                                                    ))}
                                                                </Input>
                                                            </FormGroup>
                                                        </Col>

                                                        {/* İlçe */}
                                                        <Col md={6}>
                                                            <FormGroup>
                                                                <Label>İlçe</Label>
                                                                <Input
                                                                    type="select"
                                                                    name="district"
                                                                    value={district}
                                                                    onChange={handleChange}
                                                                    disabled={!city}
                                                                >
                                                                    <option value="">İlçe seçiniz</option>
                                                                    {districtOptions.map((dist) => (
                                                                        <option key={dist} value={dist}>
                                                                            {dist}
                                                                        </option>
                                                                    ))}
                                                                </Input>
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>
                                                    <FormGroup>
                                                        <Label>Adres</Label>
                                                        <Input
                                                            type="textarea"
                                                            name="address"
                                                            rows={3}
                                                            value={form.address || ""}
                                                            onChange={handleChange}
                                                            maxLength={255}
                                                            invalid={!!errors.address}
                                                        />
                                                        {errors.address && <div className="invalid-feedback d-block">{errors.address}</div>}
                                                    </FormGroup>

                                                    {/* Adres alanını faturanın altına aldık
                                                    <FormGroup>
                                                        <Label>Adres (Genel)</Label>
                                                        <Input type="textarea" name="address" rows={3} value={form.address || ""} onChange={handleChange} maxLength={255} invalid={!!errors.address} />
                                                        {errors.address && <div className="invalid-feedback d-block">{errors.address}</div>}
                                                    </FormGroup>
                                                    */}

                                                    {/* 4) Teknik Bilgiler (Domain/Subdomain + Kur ayarları + Görseller) */}
                                                    <hr className="my-4" />
                                                    <h5 className="text-muted mb-3">Teknik Bilgiler</h5>
                                                    <FormGroup>
                                                        <Label>Alt Domain *</Label>
                                                        <Input name="subdomain" value={form.subdomain} onChange={handleChange} maxLength={100} invalid={!!errors.subdomain} />
                                                        <FormText>
                                                            Alt alan adı 3–100 karakter olmalı; küçük harf, rakam ve tire (-) içerebilir.
                                                            Belirttiğiniz alt alan adı tahsilat360.com’un önüne eklenerek <strong>test.tahsilat360.com</strong> formatında kullanılır.
                                                        </FormText>
                                                        {errors.subdomain && <div className="invalid-feedback d-block">{errors.subdomain}</div>}
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>Domain *</Label>
                                                        <Input name="domain" value={form.domain} onChange={handleChange} maxLength={100} invalid={!!errors.domain} />
                                                        <FormText>Harf, sayı, nokta ve tire (3-100)</FormText>
                                                        {errors.domain && <div className="invalid-feedback d-block">{errors.domain}</div>}
                                                    </FormGroup>

                                                    <FormGroup switch className="mb-4">
                                                        <Input type="switch" name="fetchRatesAutomatically" checked={!!form.fetchRatesAutomatically} onChange={handleChange} />
                                                        <Label check>Döviz Kurlarını Otomatik Belirle (TCMB + Oran)</Label>
                                                        <FormText>Aktifken TCMB kurları üzerine belirlediğiniz oran eklenir. Pasifken final kur değerlerini siz girersiniz.</FormText>
                                                    </FormGroup>

                                                    {form.fetchRatesAutomatically ? (
                                                        <>
                                                            <h6 className="text-muted">Otomatik Kur İçin Ekleme Oranları</h6>
                                                            <Row className="g-3">
                                                                <Col md={4}>
                                                                    <FormGroup>
                                                                        <Label>Dolar (%)</Label>
                                                                        <Input type="number" step="0.001" name="usdMarkupRate" value={form.usdMarkupRate} onChange={handleChange} placeholder="0.30" />
                                                                    </FormGroup>
                                                                </Col>
                                                                <Col md={4}>
                                                                    <FormGroup>
                                                                        <Label>Euro (%)</Label>
                                                                        <Input type="number" step="0.001" name="eurMarkupRate" value={form.eurMarkupRate} onChange={handleChange} placeholder="0.30" />
                                                                    </FormGroup>
                                                                </Col>
                                                                <Col md={4}>
                                                                    <FormGroup>
                                                                        <Label>Pound (%)</Label>
                                                                        <Input type="number" step="0.001" name="gbpMarkupRate" value={form.gbpMarkupRate} onChange={handleChange} placeholder="0.30" />
                                                                    </FormGroup>
                                                                </Col>
                                                            </Row>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <h6 className="text-muted">Manuel Final Kur Değerleri</h6>
                                                            <Row className="g-3">
                                                                <Col md={4}>
                                                                    <FormGroup>
                                                                        <Label>Final USD</Label>
                                                                        <Input type="number" step="0.00001" name="finalUsdRate" value={form.finalUsdRate} onChange={handleChange} placeholder="32.85" />
                                                                    </FormGroup>
                                                                </Col>
                                                                <Col md={4}>
                                                                    <FormGroup>
                                                                        <Label>Final EUR</Label>
                                                                        <Input type="number" step="0.00001" name="finalEurRate" value={form.finalEurRate} onChange={handleChange} placeholder="35.12" />
                                                                    </FormGroup>
                                                                </Col>
                                                                <Col md={4}>
                                                                    <FormGroup>
                                                                        <Label>Final GBP</Label>
                                                                        <Input type="number" step="0.00001" name="finalGbpRate" value={form.finalGbpRate} onChange={handleChange} placeholder="41.50" />
                                                                    </FormGroup>
                                                                </Col>
                                                            </Row>
                                                        </>
                                                    )}

                                                    {/* Görseller */}
                                                    <hr className="my-4" />
                                                    <h6 className="text-muted">Görseller</h6>
                                                    <Row className="g-3">
                                                        <Col md={6}>
                                                            <FormGroup>
                                                                <Label>Logo</Label>
                                                                <div className="border rounded p-3 mb-2 text-center" style={{ height: 120, background: "#f8f9fa" }}>
                                                                    {logoPreview && !logoError ? (
                                                                        <img src={logoPreview} alt="logo" style={{ maxHeight: 100, objectFit: "contain" }} />
                                                                    ) : (
                                                                        <span className="text-muted">{logoError ? "Logo yüklenemedi" : "Logo bulunamadı"}</span>
                                                                    )}
                                                                </div>
                                                                {logoPreview && !logoError && (
                                                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                                                        <Button size="sm" className="btn-outline-navy btn-rounded"
                                                                                onClick={() => downloadBlobUrl(logoPreview, `company-${id}-logo`)}>
                                                                            <i className="bx bx-download me-1"></i> Logo İndir
                                                                        </Button>
                                                                        <Button size="sm" color="danger" outline className="btn-rounded"
                                                                                onClick={() => setPendingDelete("logo")}>
                                                                            <i className="bx bx-trash me-1"></i> Kaldır
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                                <Input type="file" accept={BRANDING_ACCEPT_ATTR} onChange={handleLogoSelect} />
                                                                <div className="text-muted small mt-2">
                                                                    <div><strong>Format:</strong> PNG, JPG, JPEG • <strong>Maks:</strong> 5 MB</div>
                                                                    {/* "Boyut önerileri" yazıyordu ve liste eksikti; bu ölçüler zorunlu. */}
                                                                    <div><strong>Ölçü:</strong> {formatAllowedDimensions("logo")}</div>
                                                                </div>
                                                            </FormGroup>
                                                        </Col>

                                                        <Col md={6}>
                                                            <FormGroup>
                                                                <Label>Favicon</Label>
                                                                <div className="border rounded p-3 mb-2 text-center" style={{ height: 120, background: "#f8f9fa" }}>
                                                                    {faviconPreview && !faviconError ? (
                                                                        <img src={faviconPreview} alt="favicon" style={{ maxHeight: 60, objectFit: "contain" }} />
                                                                    ) : (
                                                                        <span className="text-muted">{faviconError ? "Favicon yüklenemedi" : "Favicon bulunamadı"}</span>
                                                                    )}
                                                                </div>
                                                                {faviconPreview && !faviconError && (
                                                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                                                        <Button size="sm" className="btn-outline-navy btn-rounded"
                                                                                onClick={() => downloadBlobUrl(faviconPreview, `company-${id}-favicon`)}>
                                                                            <i className="bx bx-download me-1"></i> Favicon İndir
                                                                        </Button>
                                                                        <Button size="sm" color="danger" outline className="btn-rounded"
                                                                                onClick={() => setPendingDelete("favicon")}>
                                                                            <i className="bx bx-trash me-1"></i> Kaldır
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                                <Input type="file" accept={BRANDING_ACCEPT_ATTR} onChange={handleFaviconSelect} />
                                                                <div className="text-muted small mt-2">
                                                                    <div><strong>Format:</strong> PNG, JPG, JPEG • <strong>Maks:</strong> 5 MB</div>
                                                                    <div><strong>Ölçü:</strong> {formatAllowedDimensions("favicon")}</div>
                                                                </div>
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>

                                                    {/* 5) Lisans / Paket */}
                                                    <hr className="my-4" />
                                                    <h5 className="text-muted mb-3">Lisans &amp; Paket</h5>
                                                    <Row className="g-3">
                                                        <Col md={6}>
                                                            <Label>Paket</Label>
                                                            <div className="rounded-3 border px-3 d-flex align-items-center fw-semibold"
                                                                style={{ height: 38, background: "#f8fafc", fontSize: ".85rem", color: "#221C5D" }}>
                                                                <i className="bx bx-package me-2" />
                                                                {currentPlanName || "—"}
                                                            </div>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Label>Lisans Aralığı</Label>
                                                            <div className="rounded-3 border px-3 d-flex align-items-center"
                                                                style={{ height: 38, background: "#f8fafc", fontSize: ".85rem", color: "#221C5D" }}>
                                                                <i className="bx bx-calendar me-2" />
                                                                {form.licenseStartDate && form.licenseExpiryDate
                                                                    ? `${formatLicenseDate(form.licenseStartDate)} – ${formatLicenseDate(form.licenseExpiryDate)}`
                                                                    : "—"}
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <small className="text-muted d-block mt-2">
                                                        Paket değişikliği paket yönetimi ekranından yapılır.
                                                    </small>

                                                    <FormGroup className="mb-3 mt-2">
                                                        <div
                                                            className="d-flex align-items-center"
                                                            onClick={() => handleChange({ target: { name: "active", type: "checkbox", checked: !form.active } })}
                                                            style={{ cursor: "pointer", userSelect: "none" }}
                                                        >
                                                            <input className="form-check-input me-2" type="checkbox" checked={form.active} readOnly />
                                                            <label className="form-check-label" style={{ cursor: "pointer" }}>Aktif</label>
                                                        </div>
                                                    </FormGroup>

                                                    {/* Toplu form hataları */}
                                                    {Object.keys(errors).length > 0 && (
                                                        <Alert color="danger" className="mt-3 rounded-4">
                                                            <strong>Lütfen aşağıdaki hataları düzeltin:</strong>
                                                            <ul className="mb-0 mt-2">
                                                                {Object.values(errors).map((er, i) => <li key={i}>{er}</li>)}
                                                            </ul>
                                                        </Alert>
                                                    )}

                                                    {/* Alt aksiyonlar (mobile için tekrar) */}
                                                    <div className="d-flex justify-content-end gap-2 mt-3 d-md-none">
                                                        <Button color="secondary" className="rounded-pill px-4" onClick={() => navigate(-1)} disabled={saving}>
                                                            İptal
                                                        </Button>
                                                        <Button color="primary" className="rounded-pill px-4" onClick={handleSubmit} disabled={saving}>
                                                            {saving ? (<><Spinner size="sm" className="me-2" /> Kaydediliyor...</>) : (<>Kaydet</>)}
                                                        </Button>
                                                    </div>
                                                </Form>
                                            </TabPane>

                                            {!!company && (
                                                <TabPane tabId="payment">
                                                    <PaymentSettingsTab companyId={company.id} companyCode={company.companyCode} />
                                                </TabPane>
                                            )}

                                            {!!company && (
                                                <TabPane tabId="groups">
                                                    <CompanyGroupSettings companyId={company.id} />
                                                </TabPane>
                                            )}

                                            {/* YENİ: Sözleşmeler tab’ı */}
                                            <TabPane tabId="contracts">
                                                <Form>
                                                    <h5 className="text-muted mb-3">Sözleşmeler</h5>

                                                    <FormGroup>
                                                        <Label>İptal / İade Koşulları & Gizlilik</Label>
                                                        <Input
                                                            type="textarea"
                                                            name="cancellationReturnPrivacy"
                                                            value={form.cancellationReturnPrivacy || ""}
                                                            onChange={handleChange}
                                                            rows={8}
                                                            maxLength={10000}
                                                            invalid={!!errors.cancellationReturnPrivacy}
                                                            style={{ fontFamily: "monospace", fontSize: 14 }}
                                                            placeholder="İptal/iade koşulları + gizlilik..."
                                                        />
                                                        <FormText>
                              <span className={(form.cancellationReturnPrivacy || "").length > 9000 ? "text-warning" : "text-muted"}>
                                {(form.cancellationReturnPrivacy || "").length} / 10.000 karakter
                              </span>
                                                        </FormText>
                                                        {errors.cancellationReturnPrivacy && <div className="invalid-feedback d-block">{errors.cancellationReturnPrivacy}</div>}
                                                        <div className="text-end mt-2">
                                                            <Button size="sm" color="secondary" disabled={sampleLoading} onClick={() => loadSampleContract("cancellationReturnPrivacy")}>
                                                                Örnek Sözleşme Yükle
                                                            </Button>
                                                        </div>
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>Kişisel Veriler Politikası (KVKK)</Label>
                                                        <Input
                                                            type="textarea"
                                                            name="personalDataPolicy"
                                                            value={form.personalDataPolicy || ""}
                                                            onChange={handleChange}
                                                            rows={8}
                                                            maxLength={10000}
                                                            invalid={!!errors.personalDataPolicy}
                                                            style={{ fontFamily: "monospace", fontSize: 14 }}
                                                            placeholder="KVKK politikası..."
                                                        />
                                                        <FormText>
                              <span className={(form.personalDataPolicy || "").length > 9000 ? "text-warning" : "text-muted"}>
                                {(form.personalDataPolicy || "").length} / 10.000 karakter
                              </span>
                                                        </FormText>
                                                        {errors.personalDataPolicy && <div className="invalid-feedback d-block">{errors.personalDataPolicy}</div>}
                                                        <div className="text-end mt-2">
                                                            <Button size="sm" color="secondary" disabled={sampleLoading} onClick={() => loadSampleContract("personalDataPolicy")}>
                                                                Örnek Sözleşme Yükle
                                                            </Button>
                                                        </div>
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>Hizmet Sözleşmesi</Label>
                                                        <Input
                                                            type="textarea"
                                                            name="serviceContract"
                                                            value={form.serviceContract || ""}
                                                            onChange={handleChange}
                                                            rows={8}
                                                            maxLength={10000}
                                                            invalid={!!errors.serviceContract}
                                                            style={{ fontFamily: "monospace", fontSize: 14 }}
                                                            placeholder="Hizmet sözleşmesi..."
                                                        />
                                                        <FormText>
                              <span className={(form.serviceContract || "").length > 9000 ? "text-warning" : "text-muted"}>
                                {(form.serviceContract || "").length} / 10.000 karakter
                              </span>
                                                        </FormText>
                                                        {errors.serviceContract && <div className="invalid-feedback d-block">{errors.serviceContract}</div>}
                                                        <div className="text-end mt-2">
                                                            <Button size="sm" color="secondary" disabled={sampleLoading} onClick={() => loadSampleContract("serviceContract")}>
                                                                Örnek Sözleşme Yükle
                                                            </Button>
                                                        </div>
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>Online Tahsilat Sözleşmesi</Label>
                                                        <Input
                                                            type="textarea"
                                                            name="onlineCollectionContract"
                                                            value={form.onlineCollectionContract || ""}
                                                            onChange={handleChange}
                                                            rows={8}
                                                            maxLength={10000}
                                                            invalid={!!errors.onlineCollectionContract}
                                                            style={{ fontFamily: "monospace", fontSize: 14 }}
                                                            placeholder="Online tahsilat sözleşmesi..."
                                                        />
                                                        <FormText>
                              <span className={(form.onlineCollectionContract || "").length > 9000 ? "text-warning" : "text-muted"}>
                                {(form.onlineCollectionContract || "").length} / 10.000 karakter
                              </span>
                                                        </FormText>
                                                        {errors.onlineCollectionContract && <div className="invalid-feedback d-block">{errors.onlineCollectionContract}</div>}
                                                        <div className="text-end mt-2">
                                                            <Button size="sm" color="secondary" disabled={sampleLoading} onClick={() => loadSampleContract("onlineCollectionContract")}>
                                                                Örnek Sözleşme Yükle
                                                            </Button>
                                                        </div>
                                                    </FormGroup>
                                                </Form>
                                            </TabPane>

                                            {/* Komisyon hakediş oranları (firma × POS sağlayıcı) */}
                                            {!!company && isSuperAdmin && (
                                                <TabPane tabId="earning">
                                                    <CompanyEarningSettings companyId={company.id} />
                                                </TabPane>
                                            )}

                                            {/* Valör ayarları (firma × POS sağlayıcı) */}
                                            {!!company && isSuperAdmin && (
                                                <TabPane tabId="valor">
                                                    <CompanyValorSettings companyId={company.id} />
                                                </TabPane>
                                            )}
                                        </TabContent>
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* SİLME ONAY MODALI */}
            <Modal isOpen={pendingDelete !== null} toggle={() => setPendingDelete(null)} centered>
                <ModalHeader toggle={() => setPendingDelete(null)} className="border-0 pb-0">
                    Silme Onayı
                </ModalHeader>
                <ModalBody className="pt-3">
                    <p className="text-muted mb-0">
                        {pendingDelete === "favicon"
                            ? "Faviconu kaldırmak istediğinize emin misiniz? Sekme ikonu varsayılana döner."
                            : "Logoyu kaldırmak istediğinize emin misiniz? Bu işlem geri alınamaz."}
                    </p>
                </ModalBody>
                <ModalFooter className="border-0 pt-0">
                    <Button color="secondary" outline className="btn-rounded" onClick={() => setPendingDelete(null)}>
                        İptal
                    </Button>
                    <Button
                        color="danger"
                        className="btn-rounded"
                        onClick={async () => {
                            const type = pendingDelete;
                            setPendingDelete(null);
                            if (type) await handleAssetDelete(type);
                        }}
                    >
                        Kaldır
                    </Button>
                </ModalFooter>
            </Modal>

            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false}
                            newestOnTop={false} closeOnClick pauseOnFocusLoss draggable pauseOnHover />
        </div>
    );
};

export default CompanyEditPage;